import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import {
  WebAppProvider,
  useExpand,
  useWebApp,
} from "@vkruglikov/react-telegram-web-app";
import { Toaster } from "react-hot-toast";

// Pages
import Club from "./pages/Club";
import LeaderboardComponent from "./pages/Leaderboard";
import NotFoundPage from "./components/NotFoundPage";
import Profile from "./components/Profile/Profile";
import TaskPage from "./pages/Tasks";
import GamePage from "./pages/Game";
import AdminStats from "./components/Admin/AdminStats";
import AdminUsers from "./components/Admin/AdminUsers";
import AdminSettings from "./components/Admin/AdminSettings";
import GlobalMiddleware from "./middleware/GlobalMiddleware";
import AdminMiddleware from "./middleware/AdminMiddleware";
import RestrictedPage from "./pages/RestrictedPage";
import AdminNav from "./components/Admin/AdminNav";
import MaintenancePage from "./components/Maintenance";

import GameOverPage from "./pages/GameOverPage"; // <-- import the new page
import { useSelector } from "react-redux";
import { RootState } from "./redux/type";
import { calculateLifeTimeData } from "./utils/helpers";
import Store from "./pages/Store";
import AdminItems from "./components/Admin/AdminItems";
import AdminManageTasks from "./components/Admin/AdminManageTasks";

const App: React.FC = () => {
  const WebApp = useWebApp();

  // User state with lifetime info
  const user = useSelector((state: RootState) => state.userData);

  useEffect(() => {
    if (WebApp) {
      WebApp.disableVerticalSwipes();
      WebApp.expand();
      WebApp.setHeaderColor("#080607");
      WebApp.setBackgroundColor("#080607");
      WebApp.enableClosingConfirmation();
      WebApp.lockOrientation();

      WebApp.onEvent("viewportChanged", function (event) {
        if (!event.isStateStable) {
          WebApp.expand();
        }
      });
    }
  }, []);

  // If user lifetimeLoginDays === 0, block access except /gameover
  if (user && calculateLifeTimeData(user) <= 0 && user.role !== "admin") {
    return (
      <Router>
        <Toaster />
        <Routes>
          <Route path="/gameover" element={<GameOverPage stats={user} />} />
          <Route path="*" element={<Navigate to="/gameover" replace />} />
        </Routes>
      </Router>
    );
  }

  // Normal app routes if lifetime > 0 or user not loaded yet
  return (
    <Router>
      <Toaster />
      <GlobalMiddleware>
        <Routes>
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route
            path="/"
            element={
              <>
                <Navbar />
                <GamePage />
              </>
            }
          />
          <Route
            path="/task"
            element={
              <>
                <Navbar />
                <TaskPage />
              </>
            }
          />
          {/* NEW ADDITION: Route for the store */}
          <Route
            path="/store"
            element={
              <>
                <Navbar />
                <Store />
              </>
            }
          />

          <Route
            path="/leaderboard"
            element={
              <>
                <Navbar />
                <LeaderboardComponent />
              </>
            }
          />
          <Route
            path="/club"
            element={
              <>
                <Navbar />
                <Club />
              </>
            }
          />
          <Route
            path="/profile"
            element={
              <>
                <Navbar />
                <Profile />
              </>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <AdminMiddleware>
                <Routes>
                  <Route index element={<Navigate to="stats" replace />} />
                  <Route
                    path="stats"
                    element={
                      <>
                        <AdminNav />
                        <AdminStats />{" "}
                      </>
                    }
                  />
                  {/* MODIFICATION: Replace old task routes with the new consolidated one */}
                  <Route path="manage-tasks" element={<AdminManageTasks />} />

                  <Route
                    path="users"
                    element={
                      <>
                        <AdminNav />
                        <AdminUsers />{" "}
                      </>
                    }
                  />

                  {/* NEW ADDITION: Route for item management */}
                  <Route path="items" element={<AdminItems />} />

                  <Route
                    path="settings"
                    element={
                      <>
                        <AdminNav />
                        <AdminSettings />{" "}
                      </>
                    }
                  />
                </Routes>
              </AdminMiddleware>
            }
          />
          {/* Restricted Page for Users Without UID */}
          <Route path="/restricted" element={<RestrictedPage />} />

          {/* Catch-All 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </GlobalMiddleware>
    </Router>
  );
};

export default App;
